import CompanyGoogleMap from "company/detail/components/CompanyGoogleMap";
import { DoorsEdit } from "company/detail/components/DoorsEdit";
import { DoorsFormState, PostalAndVisitingAddress } from "company/detail/components/models";
import { PostalAddress, VisitingAddress } from "company/detail/components/PostalOrVisitingAddress";
import { PostalAddressForm } from "company/detail/components/PostalAddressForm";
import { StageEdit } from "company/detail/components/StageEdit";
import { VisitingAddressForm } from "company/detail/components/VisitingAddressForm";
import { convertToBankFormState, convertToFormState, convertToVenueFormState, getInstruction, getUpdateVenueInstuction } from "company/detail/utils";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import PhoneRow from "components/DetailPage/PhoneRow";
import AgeRestrictionDropdown from "components/dropdowns/reactselect/AgeRestrictionDropdown";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import VenueCategoryDropdown from "components/dropdowns/reactselect/VenueCategoryDropdown";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import Status from "components/Status/Status";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import UrlInput from "components/urlinput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import WebsiteLink from "components/Website/WebsiteLink";
import React from "react";
import { isNullOrEmptyString } from "shared/utils";
import Doors from "venue/components/Doors";
import Stage from "venue/components/Stage";
import { VenueSidePanelShowList } from "../SidePanelShowList";

interface VenueSidePanelDetailsProps {
  venue: server.dto.Venue
  refetch: () => Promise<void>
}

export const VenueSidePanelDetails = (props: VenueSidePanelDetailsProps) => {
  const [companyFormState, setCompanyFormState] = React.useState(convertToFormState(props.venue.company));
  const [venueFormState, setVenueFormState] = React.useState(convertToVenueFormState(props.venue.details));

  React.useEffect(() => {
    if (props.venue) {
      setCompanyFormState(convertToFormState(props.venue.company));
      setVenueFormState(convertToVenueFormState(props.venue.details));
      setTags(props.venue.company.tags || []);
    }
  }, [props.venue])

  const bankFormState = convertToBankFormState(props.venue.company.bank);
  const [tags, setTags] = React.useState(props.venue.company.tags || []);

  const onValueChanged = (key: string, value: any) => {
    if (key === "visitingAddress" || key === "postalAddress") {
      setCompanyFormState(prevState => ({
        ...prevState,
        address: value,
      }));
    } else if (key === "phone") {
      setCompanyFormState(prevState => ({
        ...prevState,
        phone: value,
      }));
    } else if (key === "website") {
      setCompanyFormState(prevState => ({
        ...prevState,
        website: value,
      }));
    } else if (key === "facebook") {
      setCompanyFormState(prevState => ({
        ...prevState,
        facebook: value,
      }));
    } else if (key === "capacity") {
      setVenueFormState(prevState => ({
        ...prevState,
        capacity: value ?? 0,
      }));
    }
    else if (key === "ageRestriction") {
      setVenueFormState(prevState => ({
        ...prevState,
        ageRestriction: value,
      }));
    } else if (key === "doorsOpen") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: {
          ...prevState.doors,
          open: value,
        },
      }));
    } else if (key === "doorsClose") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: {
          ...prevState.doors,
          close: value,
        },
      }));
    } else if (key === "category") {
      setVenueFormState(prevState => ({
        ...prevState,
        category: value,
      }));
    } else if (key === "stage") {
      setVenueFormState(prevState => ({
        ...prevState,
        stage: value,
      }));
    } else if (key === "vehicleAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        vehicleAccess: value,
      }));
    } else if (key === "parking") {
      setVenueFormState(prevState => ({
        ...prevState,
        parking: value,
      }));
    } else if (key === "gearAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        gearAccess: value,
      }));
    } else if (key === "power") {
      setVenueFormState(prevState => ({
        ...prevState,
        power: value,
      }));
    } else if (key === "lighting") {
      setVenueFormState(prevState => ({
        ...prevState,
        lighting: value,
      }));
    } else if (key === "dressingRooms") {
      setVenueFormState(prevState => ({
        ...prevState,
        dressingRooms: value,
      }));
    } else if (key === "noiseRestrictions") {
      setVenueFormState(prevState => ({
        ...prevState,
        noiseRestrictions: value,
      }));
    } else if (key === "doors") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: value
      }));
    } else if (key === "status") {
      setCompanyFormState(prevState => ({
        ...prevState,
        status: value,
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "owner") {
      setCompanyFormState(prevCompany => ({
        ...prevCompany,
        owner: value,
      }));
    }
  }

  const hasMultipleStatuses = site.currentEnvironment.statuses.company.length > 1;
  const hasAnyTags = useHasAnyTags("company");
  const hasLandlineNumbers = !isNullOrEmptyString(companyFormState.phone);
  const canInPageEdit = props.venue.company.permissions.canModify;

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onSuccessfulUpdate={props.refetch}
    onValueChanged={onValueChanged}
    fields={[{
      key: "isDeleted",
      label: "",
      hideLabel: true,
      hidden: !props.venue.company.isDeleted,
      render: () => <DeletedRow />
    }, {
      key: "isValidated",
      label: "",
      hideLabel: true,
      hidden: props.venue.company.isValidated,
      render: () => <NotValidatedRow canApprove={canInPageEdit} approveEndpoint={() => window.CoreApi.Company.Validate({
        companyId: props.venue.company.companyId
      })} />
    }, {
      key: "visitingAddress",
      label: RESX.Company.VisitingAddress,
      render: () => <VisitingAddress company={props.venue.company} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <VisitingAddressForm
          noLeftPadding
          submit={submit}
          onChange={onChange}
          value={value}
          isSaving={isSaving}
        />,
        initialValue: companyFormState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "postalAddress",
      label: RESX.Company.PostalAddress,
      render: () => <PostalAddress company={props.venue.company} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <PostalAddressForm
          noLeftPadding
          value={value}
          onChange={onChange}
          submit={submit}
          isSaving={isSaving}
        />,
        initialValue: companyFormState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "phone",
      label: RESX.Company.Phone,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(companyFormState.phone)} />,
      isEmpty: !hasLandlineNumbers,
      inPageEdit: {
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <PhoneInput
            focusOnMount
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.phone,
        submitChange: (value: string) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            phoneNumbers: convertToPhoneNumbers(value),
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "website",
      label: RESX.GeneralLabels.Website,
      render: () => <WebsiteLink website={companyFormState.website} />,
      isEmpty: !companyFormState.website,
      inPageEdit: {
        render: ({ value, onChange, cancel, submit }) => (
          <UrlInput
            focusOnMount
            value={value}
            onValueChange={onChange}
            validatorKey="companyWebsite"
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.url.maxUrlLength}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.website,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            website: value,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "facebook",
      label: "Facebook",
      render: () => <WebsiteLink website={companyFormState.facebook} />,
      isEmpty: !companyFormState.facebook,
      inPageEdit: {
        render: ({ value, onChange, cancel, submit }) => (
          <UrlInput
            focusOnMount
            value={value}
            onValueChange={onChange}
            validatorKey="companyFacebook"
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.url.maxFacebookLength}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.facebook,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            facebook: value,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "capacity",
      label: RESX.Venue.Capacity,
      render: () => <div>{venueFormState.capacity} PAX</div>,
      isEmpty: !venueFormState.capacity,
      inPageEdit: {
        initialValue: venueFormState.capacity,
        render: ({ value, onChange, cancel }) => (
          <OptionalNumberInput
            onEnterPressed={(e) => {
              onChange(e, true);
            }}
            onEscPressed={cancel}
            autoFocus
            value={value}
            onChange={onChange}
            minValue={0}
            maxValue={serverReference.constants.venue.maxCapacity}
            canBeEmpty
            decimalPoints={0}
            labelContent="PAX"
            width={100}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: number) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { capacity: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "ageRestriction",
      label: RESX.Venue.AgeRestrictions,
      render: () => <div>{venueFormState.ageRestriction.text}</div>,
      isEmpty: venueFormState.ageRestriction.value === "unknown",
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.ageRestriction,
        render: ({ value, onChange }) => (
          <AgeRestrictionDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.AgeRestriction) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { ageRestriction: value.value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "doors",
      label: RESX.Venue.Doors,
      render: () => <Doors doors={venueFormState.doors} />,
      isEmpty: !venueFormState.doors.open && !venueFormState.doors.close,
      inPageEdit: {
        initialValue: venueFormState.doors,
        render: ({ value, onChange, }) => (
          <DoorsEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: DoorsFormState) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, {
            doors: {
              open: value.open?.format("HH:mm"),
              close: value.close?.format("HH:mm")
            }
          });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "category",
      label: RESX.Venue.Category,
      render: () => <div>{venueFormState.category.text || ""}</div>,
      isEmpty: venueFormState.category.value === "unknown",
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.category,
        render: ({ value, onChange }) => (
          <VenueCategoryDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.VenueCategory) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { category: value.value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "stage",
      label: RESX.Venue.Stage,
      render: () => <Stage stage={venueFormState.stage} />,
      isEmpty: !venueFormState.stage.depth && !venueFormState.stage.width && !venueFormState.stage.toFloor && !venueFormState.stage.toCeiling,
      inPageEdit: {
        initialValue: venueFormState.stage,
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => (
          <div style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">
            <StageEdit
              stage={value}
              onChange={onChange}
              submit={submit}
              isSaving={isSaving}
            />
          </div>
        ),
        type: "custom",
        submitChange: async (value: server.dto.Stage) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { stage: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "vehicleAccess",
      label: RESX.Venue.VehicleAccess,
      render: () => <div>{venueFormState.vehicleAccess}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.vehicleAccess),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.vehicleAccess,
        render: ({ value, onChange, cancel, submit }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxVehicleAccessLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { vehicleAccess: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "parking",
      label: RESX.Venue.Parking,
      render: () => <div>{venueFormState.parking}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.parking),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.parking,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxParkingLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { parking: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "gearAccess",
      label: RESX.Venue.GearAccess,
      render: () => <div>{venueFormState.gearAccess}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.gearAccess),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.gearAccess,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxGearAccessLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { gearAccess: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "power",
      label: RESX.Venue.Power,
      render: () => <div>{venueFormState.power}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.power),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.power,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxPowerLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { power: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "lighting",
      label: RESX.Venue.Lighting,
      render: () => <div>{venueFormState.lighting}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.lighting),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.lighting,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxLightingLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { lighting: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "dressingRooms",
      label: RESX.Venue.DressingRooms,
      render: () => <div>{venueFormState.dressingRooms}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.dressingRooms),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.dressingRooms,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxDressingRoomsLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { dressingRooms: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "noiseRestrictions",
      label: RESX.Venue.NoiseRestrictions,
      render: () => <div>{venueFormState.noiseRestrictions}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.noiseRestrictions),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.noiseRestrictions,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxNoiseRestrictionsLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(props.venue.company.companyId, venueFormState, { noiseRestrictions: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={companyFormState.status} />,
      hidden: !hasMultipleStatuses,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="company"
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            statusId: value.statusId,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={companyFormState.owner} />,
      isEmpty: !companyFormState.owner,
      inPageEdit: {
        hoverEdit: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: companyFormState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(props.venue.company, companyFormState, bankFormState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "tags",
      label: RESX.Tag.Tags,
      hidden: !hasAnyTags,
      isEmpty: tags.length === 0,
      render: () => <TagList tags={tags} length="short" />,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <TagsSelect
            mainArea="company"
            value={value}
            onChange={onChange}
            inPageEditing="edit"
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Company.UpdateTags({
            itemId: props.venue.company.companyId,
            tagsIds: tags.map(tag => tag.tagId),
          });
        }
      }
    }]}
    additionalContent={<>
      <ViewOnMap height={400}>
        <CompanyGoogleMap company={props.venue.company} />
      </ViewOnMap>

      <VenueSidePanelShowList venueId={props.venue.company.companyId} />
    </>}
  />
}